import { FC, useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Alert,
  Autocomplete,
  AutocompleteRenderInputParams,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import { isEmpty } from "lodash";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useSetAbsenceTypeAsMain } from "src/Pages/Company/Absences/api/absenceTypes.queries";
import { IAbsenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

interface IAbsenceTypeSelectMainFormValues {
  absenceType: { id: string; label: string; disabled: boolean } | null;
}

interface IAbsenceTypesSettingsProps {
  absenceTypes: IAbsenceType[];
  resetModal: VoidFunction;
}

const AbsenceTypesSettings: FC<IAbsenceTypesSettingsProps> = ({
  absenceTypes,
  resetModal,
}) => {
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );

  const [showAlert, setShowAlert] = useState(false);

  const prepareOption = useCallback(
    (item: IAbsenceType) => ({
      id: item.id,
      label: item.name,
    }),
    []
  );

  const mainAbsenceTypeId = absenceTypes.find((o) => o.isMain)?.id;

  const absenceTypesOptions = useMemo(
    () =>
      absenceTypes
        .filter(
          (o) =>
            o.active &&
            o.billingCycleConfiguration &&
            !o.billingCycleConfiguration?.parentAbsenceTypeId
        )
        .map(prepareOption),
    [absenceTypes, prepareOption]
  );

  const defaultValue =
    absenceTypesOptions.find((o) => o.id === mainAbsenceTypeId) || null;

  const { control, handleSubmit } = useForm<IAbsenceTypeSelectMainFormValues>({
    defaultValues: { absenceType: defaultValue },
  });

  const companyId = useCompanyId();
  const absenceMainMutation = useSetAbsenceTypeAsMain(companyId);

  const onSubmit = (formData: IAbsenceTypeSelectMainFormValues) => {
    const { absenceType } = formData;

    if (!absenceType?.id) {
      setShowAlert(true);
    } else {
      absenceMainMutation.mutate({ absenceTypeId: absenceType?.id || null });
      resetModal();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Controller
        name="absenceType"
        control={control}
        render={({
          field: { value, onChange, ref },
          fieldState: { invalid, error },
        }) => (
          <Autocomplete
            fullWidth
            options={absenceTypesOptions || []}
            value={value || null}
            isOptionEqualToValue={(option, selectedValue) =>
              option.id === selectedValue?.id
            }
            getOptionLabel={(option) => (!isEmpty(option) ? option.label : "")}
            onChange={(__, newValue) => {
              onChange(newValue);
            }}
            disabled={!canWriteAbsencesConfiguration}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextField
                {...params}
                error={invalid}
                helperText={error?.message}
                inputRef={ref}
                data-testid="main-absence-type-form-field__id"
              />
            )}
          />
        )}
      />
      <Stack direction="row" justifyContent="flex-end" spacing={2} mt="2rem">
        <Button variant="outlined" size="large" onClick={resetModal}>
          {__("Anuluj")}
        </Button>
        <Button
          variant="contained"
          size="large"
          type="submit"
          loading={absenceMainMutation.isPending}
          data-testid="main-absence-type-form__button-submit"
        >
          {__("Zapisz")}
        </Button>
      </Stack>
      {showAlert && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                absenceMainMutation.mutate({ absenceTypeId: null });
                resetModal();
              }}
            >
              {__("Tak, odepnij")}
            </Button>
          }
          sx={{
            padding: "0.375rem 1rem !important",
            marginTop: "1.5rem",
          }}
        >
          {__("Czy na pewno chcesz odpiąć szablon wskazany jako wypoczynkowy?")}
        </Alert>
      )}
    </form>
  );
};

export default AbsenceTypesSettings;
